import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileJson2, Loader2, Pencil, Plus, Radio, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { mapChannelRow, sortChannels, visibilityLabel, fetchSubscriberCountsByChannel, type ChannelRow } from '../../lib/channels';
import ChannelModal from './ChannelModal';

export default function ChannelsView() {
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [subscriberCountsByChannel, setSubscriberCountsByChannel] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ChannelRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [channelsResult, countsByChannel] = await Promise.all([
        supabase.from('channels').select('*').order('sort_order', { ascending: true }),
        fetchSubscriberCountsByChannel(),
      ]);
      if (channelsResult.error) throw channelsResult.error;
      setRows(sortChannels((channelsResult.data ?? []).map((r) => mapChannelRow(r as Record<string, unknown>))));
      setSubscriberCountsByChannel(countsByChannel);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load channels');
      setRows([]);
      setSubscriberCountsByChannel({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.slug.toLowerCase().includes(q) ||
        r.label.toLowerCase().includes(q) ||
        r.visibility.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Radio className="h-7 w-7 text-emerald-500" />
            Channels
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Audience pathways. Public channels appear on signup; private channels (e.g. Pickles, Taco, Tech) are
            admin-assigned and read-only on subscriber profiles.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/debug/channels"
            title="Debug JSON"
            aria-label="Debug JSON"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <FileJson2 className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            title="New channel"
            aria-label="New channel"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          placeholder="Search by slug, label, or visibility…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No channels yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Subscribers</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Visibility</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((row) => (
                <tr key={row.id} className="bg-white dark:bg-gray-800">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{row.label}</div>
                    <div className="mt-0.5 text-xs font-normal text-gray-500 dark:text-gray-400 font-mono">
                      ({row.slug})
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-900 dark:text-gray-100">
                    {(subscriberCountsByChannel[row.slug] ?? 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={row.description ?? ''}>
                    {row.description ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{visibilityLabel(row.visibility)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        row.active
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {row.active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(row);
                        setModalOpen(true);
                      }}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                      title="Edit channel"
                      aria-label="Edit channel"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ChannelModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        channel={editing}
        onSuccess={() => void load()}
      />
    </div>
  );
}
